import { RunContextApp } from "framework/RCApp";
import { ErrorHandler } from "./pilotAPI";
import { createCxApiClient } from "./cxEditorAPI";
import { DataScenario as CxDataScenario } from "types/cx-editor";
export { getFuelTypeFromTemplateKey } from "utils/templates-from-feature-metrics";

/**
 * Full Data Scenario type from API response
 */
export interface DataScenarioResponse {
  id: number;
  documentId: string;
  pilotId: string;
  templateName: string;
  scenarioId: string;
  scenarioDisplayName: string;
  scenarioDocumentation?: string;
  scenarioLocale: string;
  fuelType: string;
  productType: string;
  elementNames: string[];
  defaultScenario: boolean;
  userClusters: string[];
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

/**
 * Filtered Data Scenario type for UI components
 * Excludes: createdAt, documentId, publishedAt, updatedAt
 */
export interface DataScenario {
  id: number;
  pilotId: string;
  templateName: string;
  scenarioId: string;
  scenarioDisplayName: string;
  scenarioDocumentation?: string;
  scenarioLocale: string;
  fuelType: string;
  productType: string;
  elementNames: string[];
  defaultScenario: boolean;
  userClusters: string[];
}

/**
 * Transform API response to filtered DataScenario
 */
export const transformToDataScenario = (response: DataScenarioResponse): DataScenario => ({
  id: response.id,
  pilotId: response.pilotId,
  templateName: response.templateName,
  scenarioId: response.scenarioId,
  scenarioDisplayName: response.scenarioDisplayName,
  scenarioDocumentation: response.scenarioDocumentation,
  scenarioLocale: response.scenarioLocale,
  fuelType: response.fuelType,
  productType: response.productType,
  elementNames: response.elementNames,
  defaultScenario: response.defaultScenario,
  userClusters: response.userClusters,
});

/** Pass the full template key to the API  */
export const getTemplateNameForApi = (templateKey: string): string => {
  return templateKey;
};



/**
 * Payload item for creating data scenario
 */
export interface ScenarioPayload {
  templateTypeName: string;
  payload: Record<string, unknown>;
}

/**
 * Request body for creating a data scenario with payloads
 */
export interface CreateDataScenarioRequest {
  pilotId: string;
  scenarioId: string;
  scenarioDisplayName: string;
  productType: string;
  scenarioLocale: string;
  fuelType: string;
  templateName: string;
  elementNames: string[];
  defaultScenario: boolean;
  userClusters: string[];
  scenarioDocumentation?: string;
  payloads: ScenarioPayload[];
}


export interface DataScenarioDetailsResponse {
  defaultScenario: boolean;
  documentId: string;
  elementNames: string[];
  fuelType: string;
  payloads: Array<{
    documentId: string;
    payload: Record<string, unknown>;
    templateTypeName: string;
  }>;
  productType: string;
  scenarioDisplayName: string;
  scenarioDocumentation?: string;
  scenarioId: string;
  scenarioLocale: string;
  templateName: string;
  userClusters: string[];
}

export interface DeleteDataScenarioResponse {
  message: string;
  payloadsDeleted: number;
  scenarioDeleted: boolean;
}

/**
 * Data Scenarios API - follows the same pattern as PilotApis
 */
export const DataScenariosApi = {

  getDataScenarios: async (
    rc: RunContextApp,
    params: { templateName: string; pilotId: string; forceRefresh?: boolean; fuelType?: string }
  ): Promise<{ success: boolean; data?: DataScenarioResponse[]; error?: string }> => {
    try {
      const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
      const response = await api.getDataScenariosByTemplate(params.templateName, params.pilotId, params.forceRefresh, params.fuelType);

      // Transform CxDataScenario[] to DataScenarioResponse[]
      const data: DataScenarioResponse[] = (response.dataScenarios || []).map((scenario: CxDataScenario) => ({
        id: scenario.id,
        documentId: scenario.documentId,
        pilotId: scenario.pilotId || params.pilotId,
        templateName: scenario.templateName,
        scenarioId: scenario.scenarioId,
        scenarioDisplayName: scenario.scenarioDisplayName,
        scenarioDocumentation: scenario.scenarioDocumentation,
        scenarioLocale: scenario.scenarioLocale,
        fuelType: scenario.fuelType,
        productType: scenario.productType,
        elementNames: scenario.elementNames || [],
        defaultScenario: scenario.defaultScenario,
        userClusters: Array.isArray(scenario.userClusters) ? scenario.userClusters : [],
        createdAt: scenario.createdAt,
        updatedAt: scenario.updatedAt,
        publishedAt: scenario.publishedAt,
      }));

      return {
        success: true,
        data: data
      };
    } catch (error: any) {
      const isNoDataFound = 
        error?.response?.status === 404 || 
        error?.response?.data?.error === 'No data scenarios found' ||
        error?.message?.includes('404');
      
      if (isNoDataFound) {
        return {
          success: true,
          data: []
        };
      }
      
      return {
        success: false,
        error: ErrorHandler.handleApiError(error, {
          context: 'Fetch data scenarios',
          showToast: true
        })
      };
    }
  },

  /**
   * Create a new data scenario with payloads
   * POST /api/data-scenario/v1/create-with-payloads
   */
  createDataScenarioWithPayloads: async (
    rc: RunContextApp,
    requestData: CreateDataScenarioRequest
  ): Promise<{ success: boolean; data?: DataScenarioResponse; error?: string }> => {
    try {
      const response = await rc.bffClient.createDataScenarioWithPayloads(requestData);

      // Check if response has error property
      if ('error' in response && response.error) {
        return {
          success: false,
          error: ErrorHandler.handleApiError(response.error, {
            context: 'Create data scenario',
            showToast: false
          })
        };
      }

      // Handle successful response
      return {
        success: true,
        data: (response as any).data?.data
      };
    } catch (error) {
      return {
        success: false,
        error: ErrorHandler.handleApiError(error, {
          context: 'Create data scenario',
          showToast: true
        })
      };
    }
  },

  getDataScenarioDetails: async (
    rc: RunContextApp,
    params: { pilotId: string; scenarioId: string }
  ): Promise<{ success: boolean; data?: DataScenarioDetailsResponse; error?: string }> => {
    try {
      const response = await rc.bffClient.getDataScenarioDetails(params);

      if ('error' in response && response.error) {
        return {
          success: false,
          error: ErrorHandler.handleApiError(response.error, {
            context: 'Fetch scenario details',
            showToast: false
          })
        };
      }

      return {
        success: true,
        data: (response as any).data?.data
      };
    } catch (error) {
      return {
        success: false,
        error: ErrorHandler.handleApiError(error, {
          context: 'Fetch scenario details',
          showToast: true
        })
      };
    }
  },

  deleteDataScenario: async (
    rc: RunContextApp,
    params: { pilotId: string; scenarioId: string }
  ): Promise<{ success: boolean; data?: DeleteDataScenarioResponse; error?: string }> => {
    try {
      const response = await rc.bffClient.deleteDataScenario(params);

      if ('error' in response && response.error) {
        return {
          success: false,
          error: ErrorHandler.handleApiError(response.error, {
            context: 'Delete data scenario',
            showToast: false
          })
        };
      }

      return {
        success: true,
        data: (response as any).data?.data
      };
    } catch (error) {
      return {
        success: false,
        error: ErrorHandler.handleApiError(error, {
          context: 'Delete data scenario',
          showToast: true
        })
      };
    }
  }
};
